import type { SocialIconName } from '../components/SocialIcon'

export type SocialLinkSetting = {
  id: string
  label: string
  href: string
  icon: SocialIconName
  enabled: boolean
  order: number
}

export type ContactSettings = {
  phoneDisplay: string
  phoneHref: string
  whatsappDisplay: string
  whatsappHref: string
  email: string
  directionsUrl: string
  mapIframeUrl: string
  socials: SocialLinkSetting[]
}

const STORAGE_KEY = 'modi_contact_settings_v1'
const listeners = new Set<(settings: ContactSettings) => void>()

export const defaultContactSettings: ContactSettings = {
  phoneDisplay: 'Telefon',
  phoneHref: 'tel:+902260000000',
  whatsappDisplay: 'WhatsApp',
  whatsappHref: 'https://wa.me/902260000000',
  email: 'info@modicafe.com',
  directionsUrl: 'https://www.google.com/maps/search/?api=1&query=Modi%20Cafe%20Yalova%20Termal',
  mapIframeUrl: 'https://maps.google.com/maps?q=Modi%20Cafe%20Yalova%20Termal&t=m&z=15&output=embed&iwloc=near',
  socials: [
    { id: 'instagram', label: 'Instagram', href: '#', icon: 'instagram', enabled: true, order: 10 },
    { id: 'facebook', label: 'Facebook', href: '#', icon: 'facebook', enabled: true, order: 20 },
  ],
}

export function getContactSettings() {
  return normalizeSettings(readLocalSettings())
}

export function saveContactSettings(settings: ContactSettings) {
  const normalized = normalizeSettings(settings)
  writeLocalSettings(normalized)
  listeners.forEach((listener) => listener(normalized))
  saveContactSettingsToFirebase(normalized).catch(() => undefined)
}

export function subscribeContactSettings(listener: (settings: ContactSettings) => void) {
  listeners.add(listener)
  listener(getContactSettings())

  let unsubscribe: (() => void) | undefined

  import('firebase/firestore')
    .then(async (firestoreModule) => {
      const firebaseModule = await import('./firebase')
      unsubscribe = firestoreModule.onSnapshot(
        firestoreModule.doc(firebaseModule.firestore, 'siteSettings', 'contact'),
        (snapshot) => {
          if (!snapshot.exists()) return
          const settings = normalizeSettings(snapshot.data() as Partial<ContactSettings>)
          writeLocalSettings(settings)
          listener(settings)
        },
        () => undefined,
      )
    })
    .catch(() => undefined)

  return () => {
    listeners.delete(listener)
    unsubscribe?.()
  }
}

function normalizeSettings(settings: Partial<ContactSettings> | null | undefined): ContactSettings {
  return {
    phoneDisplay: settings?.phoneDisplay ?? defaultContactSettings.phoneDisplay,
    phoneHref: settings?.phoneHref ?? defaultContactSettings.phoneHref,
    whatsappDisplay: settings?.whatsappDisplay ?? defaultContactSettings.whatsappDisplay,
    whatsappHref: settings?.whatsappHref ?? defaultContactSettings.whatsappHref,
    email: settings?.email ?? defaultContactSettings.email,
    directionsUrl: settings?.directionsUrl ?? defaultContactSettings.directionsUrl,
    mapIframeUrl: settings?.mapIframeUrl ?? defaultContactSettings.mapIframeUrl,
    socials:
      settings?.socials && settings.socials.length > 0
        ? settings.socials
            .map((social, index) => ({
              id: social.id || crypto.randomUUID(),
              label: social.label || 'Sosyal Medya',
              href: social.href || '#',
              icon: social.icon || 'instagram',
              enabled: social.enabled ?? true,
              order: social.order ?? (index + 1) * 10,
            }))
            .sort((first, second) => first.order - second.order)
        : defaultContactSettings.socials,
  }
}

function readLocalSettings(): ContactSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as ContactSettings) : defaultContactSettings
  } catch {
    return defaultContactSettings
  }
}

function writeLocalSettings(settings: ContactSettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // localStorage may be unavailable.
  }
}

async function saveContactSettingsToFirebase(settings: ContactSettings) {
  const firestoreModule = await import('firebase/firestore')
  const firebaseModule = await import('./firebase')
  await firebaseModule.ensureFirebaseAuth()
  await firestoreModule.setDoc(
    firestoreModule.doc(firebaseModule.firestore, 'siteSettings', 'contact'),
    settings,
  )
}
